import { FC, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslations } from '@altermaind/core/i18n';
import { Box } from '@altermaind/web/ui';
import { useDrawer } from '@/_shared/components';
import { CreditViewValue } from '@/_shared/components/CreditViewValue';
import { Column, usePaginatedTable } from '@/_shared/components/Table';
import { PaginatedTable } from '@/_shared/components/Table/PaginatedTable';
import { StyledTable, StyledTableDrawer } from '@/_shared/components/Table/StyledTable';
import { TableHeader } from '@/_shared/components/TableHeader';
import { ValueInTable } from '@/_shared/components/ValueInTable';
import { getEmployerCreditsHistoryCall } from '@/credits/state/api';
import { PAGE_SIZE_EMPLOYER_CREDITS_HISTORY } from '@/people/state/constants';
import { FilterHistory } from './FilterHistory';

export const HistoryList: FC = () => {
  const t = useTranslations('people.details.credits.history.table');
  const tShared = useTranslations('credits');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [filters, setFilters] = useState<{ month?: number; year?: number }>();
  const { isOpen, close, ...drawerProps } = useDrawer();

  const {
    execute: executeGetEmployerCreditsHistory,
    loading: loadingGetEmployerCreditsHistory,
    data,
    totalItems,
  } = getEmployerCreditsHistoryCall.hook();

  const paginedProps = usePaginatedTable({
    rowData: data || [],
    pageSize: PAGE_SIZE_EMPLOYER_CREDITS_HISTORY,
    totalItems: totalItems || 0,
  });

  const drawers: StyledTableDrawer['instances'] = [];

  const columns: Column[] = [
    {
      key: 'activity',
      title: t('activity'),
      renderValue: item => <ValueInTable>{`${tShared(`${item.eventType}`)}`}</ValueInTable>,
    },
    {
      key: 'credits',
      title: t('credits'),
      renderValue: item => (
        <ValueInTable color={item.credits > 0 ? 'success' : 'error/bright'}>
          <CreditViewValue value={item.credits} eventType={item.eventType} />
        </ValueInTable>
      ),
    },
    {
      key: 'date',
      title: t('date'),
      renderValue: item => (
        <ValueInTable>{dayjs(item.timestamp).format('DD/MM/YYYY')}</ValueInTable>
      ),
    },
  ];

  useEffect(() => {
    // Se filters?.month è una stringa come "january", convertila in numero (1-12)
    executeGetEmployerCreditsHistory({
      start: 0,
      length: PAGE_SIZE_EMPLOYER_CREDITS_HISTORY,
      year: filters?.year || dayjs().year(),
      month: Number(filters?.month) || undefined,
    });
  }, [executeGetEmployerCreditsHistory, filters]);

  return (
    <Box overflow='visible' mt='m' display='flex' flexDirection='column'>
      <TableHeader
        title={t('title')}
        filtersComponent={<FilterHistory setFilters={setFilters} filters={filters} />}
      />
      <StyledTable hasElement drawer={{ ...drawerProps, isOpen, close, instances: drawers }}>
        <PaginatedTable
          loading={loadingGetEmployerCreditsHistory}
          header-data-testid='table-current-user-credits-history-header'
          row-data-testid='table-current-user-credits-history-row'
          columns={columns}
          pageSize={PAGE_SIZE_EMPLOYER_CREDITS_HISTORY}
          call={{ ...getEmployerCreditsHistoryCall }}
          onRowClick={() => {
            /* empty */
          }}
          rowWrapperProps={(employee: string) => {
            return employee === ''
              ? {
                  backgroundColor: 'grey/dark/background',
                  borderRadius: '10px',
                  position: 'relative',
                }
              : {};
          }}
          paginedProps={paginedProps}
          skeletonProps={{
            hasHeaderRow1: false,
            hasHeaderRow2: true,
            rows: 1,
          }}
          hasHover
        />
      </StyledTable>
    </Box>
  );
};
